import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, History, ChevronRight, CheckCircle, AlertTriangle, Calendar, Package, Search, ArrowDownToLine, ArrowUpFromLine, ArrowRightLeft, Bookmark, BookmarkX, Filter, X, User, ClipboardCheck, FileText, SkipForward, ShoppingCart, Building2, Tag, Truck, ScanLine, Receipt, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state - check URL params for initial tab
  const initialTab = searchParams.get('tab') || 'stock';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Picking lists history
  const [completedLists, setCompletedLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  
  // Scan/stock history logs
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [historyStats, setHistoryStats] = useState(null);
  
  // Purchase history logs
  const [purchaseLogs, setPurchaseLogs] = useState([]);
  const [loadingPurchaseLogs, setLoadingPurchaseLogs] = useState(true);
  const [purchaseStats, setPurchaseStats] = useState(null);
  const [purchaseFilterAction, setPurchaseFilterAction] = useState('');
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');
  const [filterToday, setFilterToday] = useState(searchParams.get('filter') === 'today');

  // Debounce ref for search
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchCompletedLists();
    fetchHistoryLogs();
    fetchHistoryStats();
    fetchPurchaseLogs();
    fetchPurchaseStats();
  }, []);

  useEffect(() => {
    fetchHistoryLogs();
  }, [filterType, filterToday]);

  useEffect(() => {
    fetchPurchaseLogs();
  }, [purchaseFilterAction, filterToday]);

  const fetchCompletedLists = async () => {
    try {
      const response = await axios.get(`${API}/picking/picking-lists/completed`);
      setCompletedLists(response.data);
    } catch (error) {
      console.error("Error fetching completed lists:", error);
    } finally {
      setLoadingLists(false);
    }
  };

  const fetchHistoryLogs = async (search = searchQuery) => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType) params.append('action_type', filterType);
      if (filterToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        params.append('date_from', today.toISOString());
      }
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/history/logs?${params.toString()}`);
      setHistoryLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching history logs:", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchHistoryStats = async () => {
    try {
      const response = await axios.get(`${API}/history/stats`);
      setHistoryStats(response.data);
    } catch (error) {
      console.error("Error fetching history stats:", error);
    }
  };

  const fetchPurchaseLogs = async (search = searchQuery) => {
    setLoadingPurchaseLogs(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (purchaseFilterAction) params.append('action', purchaseFilterAction);
      if (filterToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        params.append('date_from', today.toISOString());
      }
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/history/purchase-logs?${params.toString()}`);
      setPurchaseLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching purchase logs:", error);
    } finally {
      setLoadingPurchaseLogs(false);
    }
  };

  const fetchPurchaseStats = async () => {
    try {
      const response = await axios.get(`${API}/history/purchase-stats`);
      setPurchaseStats(response.data);
    } catch (error) {
      console.error("Error fetching purchase stats:", error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce the API call
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchHistoryLogs(value);
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery('');
    fetchHistoryLogs('');
  };

  const isToday = (isoString) => {
    if (!isoString) return false;
    const date = new Date(isoString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const filteredLists = filterToday 
    ? completedLists.filter(list => isToday(list.completed_at))
    : completedLists;

  // Apply search to picking lists
  const searchedLists = searchQuery 
    ? filteredLists.filter(list => 
        list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        list.items?.some(item => 
          item.ean?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.artikelnummer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.ordernummer?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : filteredLists;

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'IN':
        return <ArrowDownToLine className="h-4 w-4 text-green-500" />;
      case 'OUT':
        return <ArrowUpFromLine className="h-4 w-4 text-red-500" />;
      case 'MOVE':
        return <ArrowRightLeft className="h-4 w-4 text-cyan-500" />;
      case 'INVENTORY':
        return <ClipboardCheck className="h-4 w-4 text-blue-500" />;
      case 'BOOK':
        return <Bookmark className="h-4 w-4 text-amber-500" />;
      case 'UNBOOK':
        return <BookmarkX className="h-4 w-4 text-purple-500" />;
      case 'POD':
        return <FileText className="h-4 w-4 text-orange-500" />;
      case 'DELETE':
        return <Trash2 className="h-4 w-4 text-red-600" />;
      case 'ZERO':
        return <RotateCcw className="h-4 w-4 text-orange-600" />;
      default:
        return <History className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'IN':
        return 'Inleverans';
      case 'OUT':
        return 'Utleverans';
      case 'MOVE':
        return 'Flyttning';
      case 'INVENTORY':
        return 'Inventering';
      case 'BOOK':
        return 'Bokning';
      case 'UNBOOK':
        return 'Avbokning';
      case 'POD':
        return 'POD';
      case 'DELETE':
        return 'Raderad';
      case 'ZERO':
        return 'Nollställd';
      default:
        return actionType;
    }
  };

  const getActionBadgeClass = (actionType) => {
    switch (actionType) {
      case 'IN':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'OUT':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'MOVE':
        return 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30';
      case 'INVENTORY':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'BOOK':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
      case 'UNBOOK':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/30';
      case 'POD':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
      case 'DELETE':
        return 'bg-red-600/10 text-red-600 border-red-600/30';
      case 'ZERO':
        return 'bg-orange-600/10 text-orange-600 border-orange-600/30';
      default:
        return '';
    }
  };

  // Purchase history helpers
  const getPurchaseActionIcon = (action) => {
    switch (action) {
      case 'ORDER_CREATED':
        return <ShoppingCart className="h-4 w-4 text-blue-500" />;
      case 'ORDER_STATUS_CHANGED':
      case 'ORDER_ITEM_UPDATED':
        return <ShoppingCart className="h-4 w-4 text-blue-400" />;
      case 'ORDER_RECEIVED':
        return <Truck className="h-4 w-4 text-cyan-500" />;
      case 'ORDER_RECEIVE_UNDONE':
        return <Truck className="h-4 w-4 text-orange-400" />;
      case 'QUICK_RECEIVE':
        return <Truck className="h-4 w-4 text-cyan-400" />;
      case 'ORDER_DELETED':
      case 'ORDER_ITEM_REMOVED':
        return <ShoppingCart className="h-4 w-4 text-red-500" />;
      case 'PRICE_SET':
      case 'PRICE_UPDATED':
        return <Tag className="h-4 w-4 text-green-500" />;
      case 'RESERVATION_CREATED':
        return <Bookmark className="h-4 w-4 text-amber-500" />;
      case 'RESERVATION_UPDATED':
        return <Bookmark className="h-4 w-4 text-amber-400" />;
      case 'RESERVATION_DELETED':
        return <Bookmark className="h-4 w-4 text-red-400" />;
      case 'RESERVATION_CONVERTED':
        return <Bookmark className="h-4 w-4 text-emerald-500" />;
      case 'SUPPLIER_CREATED':
      case 'SUPPLIER_UPDATED':
      case 'SUPPLIER_DELETED':
      case 'SUPPLIER_FORCE_DELETED':
        return <Building2 className="h-4 w-4 text-amber-500" />;
      case 'INVOICE_SCANNED':
        return <ScanLine className="h-4 w-4 text-purple-500" />;
      default:
        return <Receipt className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPurchaseActionLabel = (action) => {
    switch (action) {
      case 'ORDER_CREATED':
        return 'Order skapad';
      case 'ORDER_STATUS_CHANGED':
        return 'Status ändrad';
      case 'ORDER_ITEM_UPDATED':
        return 'Order ändrad';
      case 'ORDER_RECEIVED':
        return 'Leverans mottagen';
      case 'ORDER_RECEIVE_UNDONE':
        return 'Mottagning ångrad';
      case 'QUICK_RECEIVE':
        return 'Snabbmottagning';
      case 'ORDER_DELETED':
        return 'Order borttagen';
      case 'ORDER_ITEM_REMOVED':
        return 'Produkt borttagen';
      case 'PRICE_SET':
        return 'Pris satt';
      case 'PRICE_UPDATED':
        return 'Pris uppdaterat';
      case 'RESERVATION_CREATED':
        return 'Bokning skapad';
      case 'RESERVATION_UPDATED':
        return 'Bokning ändrad';
      case 'RESERVATION_DELETED':
        return 'Bokning avbokad';
      case 'RESERVATION_CONVERTED':
        return 'Bokning aktiverad';
      case 'SUPPLIER_CREATED':
        return 'Leverantör skapad';
      case 'SUPPLIER_UPDATED':
        return 'Leverantör uppdaterad';
      case 'SUPPLIER_DELETED':
        return 'Leverantör borttagen';
      case 'SUPPLIER_FORCE_DELETED':
        return 'Leverantör tvångsbortagen';
      case 'INVOICE_SCANNED':
        return 'Faktura skannad';
      default:
        return action;
    }
  };

  const getPurchaseActionBadgeClass = (action) => {
    switch (action) {
      case 'ORDER_CREATED':
      case 'ORDER_STATUS_CHANGED':
      case 'ORDER_ITEM_UPDATED':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'ORDER_RECEIVED':
      case 'QUICK_RECEIVE':
        return 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30';
      case 'ORDER_RECEIVE_UNDONE':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
      case 'ORDER_DELETED':
      case 'ORDER_ITEM_REMOVED':
      case 'RESERVATION_DELETED':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'PRICE_SET':
      case 'PRICE_UPDATED':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'RESERVATION_CREATED':
      case 'RESERVATION_UPDATED':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
      case 'RESERVATION_CONVERTED':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
      case 'SUPPLIER_CREATED':
      case 'SUPPLIER_UPDATED':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
      case 'SUPPLIER_DELETED':
      case 'SUPPLIER_FORCE_DELETED':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'INVOICE_SCANNED':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/30';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6 text-primary" />
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="history-title">
                Historik
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={filterToday ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterToday(true)}
              >
                Idag
              </Button>
              <Button
                variant={!filterToday ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterToday(false)}
              >
                Alla
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Summary */}
      {historyStats && (
        <div className="border-b border-border bg-card/50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-6 text-sm overflow-x-auto">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <ArrowDownToLine className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">Inleverans idag:</span>
                <span className="font-bold">{historyStats.today.quantity_in} st</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <ArrowUpFromLine className="h-4 w-4 text-red-500" />
                <span className="text-muted-foreground">Utleverans idag:</span>
                <span className="font-bold">{historyStats.today.quantity_out} st</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <ArrowRightLeft className="h-4 w-4 text-cyan-500" />
                <span className="text-muted-foreground">Flyttningar idag:</span>
                <span className="font-bold">{historyStats.today.moves || 0}</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Bookmark className="h-4 w-4 text-amber-500" />
                <span className="text-muted-foreground">Bokningar:</span>
                <span className="font-bold">{historyStats.today.bookings}</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <BookmarkX className="h-4 w-4 text-purple-500" />
                <span className="text-muted-foreground">Avbokningar:</span>
                <span className="font-bold">{historyStats.today.unbookings}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="stock" data-testid="tab-stock">Lagerrörelse</TabsTrigger>
            <TabsTrigger value="purchase" data-testid="tab-purchase">Inköp</TabsTrigger>
            <TabsTrigger value="picking" data-testid="tab-picking">Plocklistor</TabsTrigger>
          </TabsList>

          <TabsContent value="stock" className="space-y-4">
            {/* Type filter buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={filterType === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('')}
              >
                Alla typer
              </Button>
              <Button
                variant={filterType === 'IN' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('IN')}
                className="gap-1"
              >
                <ArrowDownToLine className="h-3 w-3" />
                Inleverans
              </Button>
              <Button
                variant={filterType === 'OUT' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('OUT')}
                className="gap-1"
              >
                <ArrowUpFromLine className="h-3 w-3" />
                Utleverans
              </Button>
              <Button
                variant={filterType === 'MOVE' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('MOVE')}
                className="gap-1"
                data-testid="filter-move"
              >
                <ArrowRightLeft className="h-3 w-3" />
                Flyttning
              </Button>
              <Button
                variant={filterType === 'BOOK' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('BOOK')}
                className="gap-1"
              >
                <Bookmark className="h-3 w-3" />
                Bokning
              </Button>
              <Button
                variant={filterType === 'UNBOOK' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('UNBOOK')}
                className="gap-1"
              >
                <BookmarkX className="h-3 w-3" />
                Avbokning
              </Button>
              <Button
                variant={filterType === 'INVENTORY' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('INVENTORY')}
                className="gap-1"
                data-testid="filter-inventory"
              >
                <ClipboardCheck className="h-3 w-3" />
                Inventering
              </Button>
              <Button
                variant={filterType === 'POD' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('POD')}
                className="gap-1"
                data-testid="filter-pod"
              >
                <FileText className="h-3 w-3" />
                POD
              </Button>
              <Button
                variant={filterType === 'OTHER' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('OTHER')}
                className="gap-1"
                data-testid="filter-other"
              >
                <AlertTriangle className="h-3 w-3" />
                Övrigt
              </Button>
            </div>

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Sök EAN, artikelnummer eller referens..."
                className="pl-10 pr-10"
                data-testid="history-search"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {loadingLogs ? (
              <div className="text-center py-12 text-muted-foreground">
                Laddar historik...
              </div>
            ) : historyLogs.length === 0 ? (
              <Card className="p-12 text-center">
                <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-xl font-bold mb-2">
                  {searchQuery ? 'Inga träffar' : 'Ingen historik ännu'}
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Prova ett annat sökord' : 'Skanningar och lagerrörelser visas här'}
                </p>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="history-logs-list">
                {historyLogs.map((log) => (
                  <Card key={log.id} className="p-4" data-testid={`log-${log.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getActionIcon(log.action_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className={getActionBadgeClass(log.action_type)}>
                              {getActionLabel(log.action_type)}
                            </Badge>
                            <span className="font-mono text-sm">{log.ean}</span>
                            {log.username && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                <User className="h-3 w-3" />
                                {log.username}
                              </span>
                            )}
                          </div>
                          <p className="font-medium">
                            {log.artikelnummer || log.ean}
                            {log.produktnamn && <span className="text-muted-foreground font-normal"> - {log.produktnamn}</span>}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span><strong>{log.quantity}</strong> st</span>
                            {log.location && <span className="font-mono">{log.location}</span>}
                            {log.reference && <span>Ref: {log.reference}</span>}
                          </div>
                          {log.details && (
                            <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                        <div>{formatDate(log.timestamp).split(' ').slice(0, 3).join(' ')}</div>
                        <div className="font-mono">{formatTime(log.timestamp)}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchase History Tab */}
          <TabsContent value="purchase" className="space-y-4">
            {/* Purchase stats summary */}
            {purchaseStats && (
              <div className="flex items-center gap-6 text-sm overflow-x-auto pb-2">
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <ShoppingCart className="h-4 w-4 text-blue-500" />
                  <span className="text-muted-foreground">Ordrar idag:</span>
                  <span className="font-bold">{purchaseStats.today.orders || 0}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Tag className="h-4 w-4 text-green-500" />
                  <span className="text-muted-foreground">Priser satta:</span>
                  <span className="font-bold">{purchaseStats.today.prices || 0}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Building2 className="h-4 w-4 text-amber-500" />
                  <span className="text-muted-foreground">Leverantörer:</span>
                  <span className="font-bold">{purchaseStats.today.suppliers || 0}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Truck className="h-4 w-4 text-cyan-500" />
                  <span className="text-muted-foreground">Mottagna:</span>
                  <span className="font-bold">{purchaseStats.today.received || 0}</span>
                </div>
              </div>
            )}

            {/* Purchase filter buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={purchaseFilterAction === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('')}
              >
                Alla händelser
              </Button>
              <Button
                variant={purchaseFilterAction === 'ORDER_CREATED,ORDER_STATUS_CHANGED,ORDER_ITEM_UPDATED,ORDER_ITEM_REMOVED,ORDER_DELETED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('ORDER_CREATED,ORDER_STATUS_CHANGED,ORDER_ITEM_UPDATED,ORDER_ITEM_REMOVED,ORDER_DELETED')}
                className="gap-1"
                data-testid="filter-orders"
              >
                <ShoppingCart className="h-3 w-3" />
                Ordrar
              </Button>
              <Button
                variant={purchaseFilterAction === 'RESERVATION_CREATED,RESERVATION_UPDATED,RESERVATION_DELETED,RESERVATION_CONVERTED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('RESERVATION_CREATED,RESERVATION_UPDATED,RESERVATION_DELETED,RESERVATION_CONVERTED')}
                className="gap-1"
                data-testid="filter-bookings"
              >
                <Bookmark className="h-3 w-3" />
                Bokningar
              </Button>
              <Button
                variant={purchaseFilterAction === 'PRICE_SET,PRICE_UPDATED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('PRICE_SET,PRICE_UPDATED')}
                className="gap-1"
                data-testid="filter-prices"
              >
                <Tag className="h-3 w-3" />
                Priser
              </Button>
              <Button
                variant={purchaseFilterAction === 'SUPPLIER_CREATED,SUPPLIER_UPDATED,SUPPLIER_DELETED,SUPPLIER_FORCE_DELETED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('SUPPLIER_CREATED,SUPPLIER_UPDATED,SUPPLIER_DELETED,SUPPLIER_FORCE_DELETED')}
                className="gap-1"
                data-testid="filter-suppliers"
              >
                <Building2 className="h-3 w-3" />
                Leverantörer
              </Button>
              <Button
                variant={purchaseFilterAction === 'ORDER_RECEIVED,QUICK_RECEIVE,ORDER_RECEIVE_UNDONE' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('ORDER_RECEIVED,QUICK_RECEIVE,ORDER_RECEIVE_UNDONE')}
                className="gap-1"
                data-testid="filter-receiving"
              >
                <Truck className="h-3 w-3" />
                Mottagning
              </Button>
              <Button
                variant={purchaseFilterAction === 'INVOICE_SCANNED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPurchaseFilterAction('INVOICE_SCANNED')}
                className="gap-1"
                data-testid="filter-invoices"
              >
                <ScanLine className="h-3 w-3" />
                Fakturor
              </Button>
            </div>

            {/* Purchase search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = setTimeout(() => fetchPurchaseLogs(e.target.value), 300);
                }}
                placeholder="Sök i inköpshistorik..."
                className="pl-10 pr-10"
                data-testid="purchase-search"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); fetchPurchaseLogs(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {loadingPurchaseLogs ? (
              <div className="text-center py-12 text-muted-foreground">
                Laddar inköpshistorik...
              </div>
            ) : purchaseLogs.length === 0 ? (
              <Card className="p-12 text-center">
                <Receipt className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-xl font-bold mb-2">
                  {searchQuery ? 'Inga träffar' : 'Ingen inköpshistorik ännu'}
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Prova ett annat sökord' : 'Inköpsrelaterade händelser loggas här'}
                </p>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="purchase-logs-list">
                {purchaseLogs.map((log) => (
                  <Card key={log.id} className="p-4" data-testid={`purchase-log-${log.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getPurchaseActionIcon(log.action)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className={getPurchaseActionBadgeClass(log.action)}>
                              {getPurchaseActionLabel(log.action)}
                            </Badge>
                            {log.username && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                <User className="h-3 w-3" />
                                {log.username}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-foreground">{log.description}</p>
                          {log.entity_name && (
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">{log.entity_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                        <div>{formatDate(log.timestamp).split(' ').slice(0, 3).join(' ')}</div>
                        <div className="font-mono">{formatTime(log.timestamp)}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Picking Lists Tab */}
          <TabsContent value="picking" className="space-y-4">
            {loadingLists ? (
              <div className="text-center py-12 text-muted-foreground">
                Laddar plocklistor...
              </div>
            ) : searchedLists.length === 0 ? (
              <Card className="p-12 text-center" data-testid="empty-history">
                <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-xl font-bold mb-2">
                  {filterToday ? 'Inga avslutade plocklistor idag' : 'Ingen historik ännu'}
                </h3>
                <p className="text-muted-foreground">
                  {filterToday ? 'Avslutade plocklistor visas här' : 'Slutförda plocklistor kommer att visas här'}
                </p>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="history-list">
                {searchedLists.map((list) => (
                  <Card
                    key={list.id}
                    className="data-card cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigate(`/history/${list.id}`)}
                    data-testid={`history-item-${list.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-lg font-bold">{list.name}</h3>
                            {list.completed_by && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                <User className="h-3 w-3" />
                                {list.completed_by}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(list.completed_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Package className="h-4 w-4" />
                              {list.total_items} produkter
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="flex items-center gap-1 text-sm text-success">
                              <CheckCircle className="h-4 w-4" />
                              {list.picked_count} plockade
                            </span>
                            {(list.skipped_count || 0) > 0 && (
                              <span className="flex items-center gap-1 text-sm text-amber-500">
                                <SkipForward className="h-4 w-4" />
                                {list.skipped_count} överhoppade
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
